/**
 * PricingCards.tsx — priskort slik ibm.com/products viser «Ways to buy»
 *
 * Oppbygning (målt mot ibm.com/products/watsonx-orchestrate):
 * - Tre pakker: overskrift til venstre, tre kort til høyre (lg). Fire pakker (eller uten
 *   cardsHeading): kortene fyller hele bredden, 2 per rad på md, stablet under
 * - Anbefalt kort har blått «Anbefalt»-flagg og kraftigere blå tone
 * - Kortet er delt i to: pris + knapp, skillelinje, beskrivelse + avkrysningsliste
 * - «Alt i forrige pakke, pluss» står som første punkt i listen
 * - Fotnote under kortene
 * Alle tekster og tall kommer fra innholdsfilen.
 */

import { Checkmark } from '@carbon/icons-react';
import LandingButton from '@/components/landing/LandingButton';
import type { ProductPlans } from '@/types/product';
import SectionHeader from './SectionHeader';
import shared from './product.module.css';
import styles from './plans.module.css';

export default function PricingCards({ plans }: { plans: ProductPlans }) {
  // Uten cardsHeading fyller kortene hele bredden (ibm.com med fire pakker)
  const full = !plans.cardsHeading;
  const four = plans.items.length >= 4;
  // Riktig overskriftsnivå: h3 for pakkenavn når det ikke finnes en h3-overskrift foran kortene
  const PlanHeading = full ? 'h3' : 'h4';
  // Finnes det en fremhevet pakke med merketekst, har alle pakker en eyebrow-rad øverst (tom i de andre), så navn, pris og knapper står på linje
  const hasLabel = plans.items.some((plan) => plan.highlighted && plan.highlightLabel);
  return (
    <section id="pakker" className={`${shared.sectionAlt} ${shared.net} ${styles.tiers}`} aria-labelledby="plans-heading">
      <div className={shared.container}>
        <SectionHeader id="plans-heading" eyebrow={plans.eyebrow} heading={plans.heading}>
          <p className={shared.lead}>{plans.intro}</p>
        </SectionHeader>

        <div className={`${styles.pricing} ${full ? styles.pricingFull : ''}`}>
          {plans.cardsHeading && <h3 className={styles.pricingHeading}>{plans.cardsHeading}</h3>}

          <div className={styles.pricingCards}>
            <ul className={`${styles.plans} ${four ? styles.plans4 : ''}`} role="list">
              {plans.items.map((plan) => (
                <li key={plan.id} className={styles.planItem}>
                  <article
                    className={`${styles.plan} ${plan.highlighted ? styles.planHighlighted : ''}`}
                    aria-labelledby={`plan-${plan.id}`}
                  >
                    <div className={`${styles.planTop} ${plan.highlighted ? shared.featured : ''}`}>
                      {hasLabel &&
                        (plan.highlighted && plan.highlightLabel ? (
                          <p className={shared.featuredEyebrow}>{plan.highlightLabel}</p>
                        ) : (
                          <span className={styles.planSlot} aria-hidden="true" />
                        ))}
                      <PlanHeading id={`plan-${plan.id}`} className={styles.planName}>
                        {plan.name}
                      </PlanHeading>
                      <p className={styles.priceFrom}>{plans.fromLabel}</p>
                      <p className={styles.price}>{plan.price}</p>
                      <p className={styles.priceNote}>{plan.priceNote}*</p>
                      <div className={styles.planCta}>
                        <LandingButton
                          link={plan.cta}
                          kind={plan.highlighted ? 'primary' : 'tertiary'}
                        />
                      </div>
                    </div>

                    <div className={styles.planBody}>
                      <p className={styles.planSummary}>{plan.summary}</p>
                      <ul className={styles.featureList} role="list">
                        {plan.inheritsLabel && (
                          <li className={styles.feature}>
                            <Checkmark size={16} aria-hidden />
                            <span>{plan.inheritsLabel}:</span>
                          </li>
                        )}
                        {plan.features.map((feature) => (
                          <li key={feature} className={styles.feature}>
                            <Checkmark size={16} aria-hidden />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </article>
                </li>
              ))}
            </ul>
            <p className={styles.footnote}>{plans.footnote}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
